import { useState } from 'react';
import { Link } from 'react-router-dom';
import OAuthButtons from '../components/OAuthButtons';

// CFG.TIER.A6 — Solo-developer marketing landing page. Pure frontend
// presentation; OAuth signup behaviour is unchanged from /register.
// Backend (apps/api/src/auth/oauth.strategies.ts:104) creates fresh
// OAuth signups as `subscription_tier: 'INDIVIDUAL'` by default, so
// linking straight to the existing /auth/google + /auth/github endpoints
// produces the right tenant shape — no callback-URL distinction.
//
// #3023 — this page previously had no terms checkbox at all, so every OAuth
// signup from it created an account with no consent recorded. It now carries
// the same checkbox as /register, and the OAuth buttons stay disabled until it
// is ticked; they then start with ?terms=accepted, which the API requires
// before it creates the account. This page exists for marketing
// positioning only: a clearer "solo developer" entry surface that can
// be linked from external content without the SME/team framing of
// /register.

function BlueprintMark({ size = 48 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="10" fill="#7C3AED" />
      <path d="M12 32 L24 26 L36 32 L24 38 Z" fill="white" opacity="0.22" />
      <path d="M12 26 L24 20 L36 26 L24 32 Z" fill="white" opacity="0.55" />
      <path d="M12 20 L24 14 L36 20 L24 26 Z" fill="white" opacity="0.9" />
      <circle cx="36" cy="12" r="5" fill="#EC4899" />
    </svg>
  );
}

const valueBullets: ReadonlyArray<{ title: string; body: string }> = [
  {
    title: 'Built for one',
    body: 'A personal workspace — no team management overhead, no seat licences.',
  },
  {
    title: '1M tokens every month',
    body: 'Full agent capability for personal projects. Resets on the 1st each month.',
  },
  {
    title: 'Upgrade when ready',
    body: 'Move to Starter for team seats and 5M tokens/seat — no data migration friction.',
  },
];

export default function RegisterIndividualPage() {
  // #3023 — unchecked by default; gates the OAuth buttons below.
  const [termsAccepted, setTermsAccepted] = useState(false);
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg)' }}>
      <div style={{
        width: '100%',
        maxWidth: 460,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-lg)',
        padding: 40,
      }}>
        <div className="flex flex-col items-center mb-6">
          <BlueprintMark size={48} />
          <div className="flex items-baseline mt-3">
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.5px' }}>Blue</span>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--primary)', letterSpacing: '-0.5px' }}>Print</span>
          </div>
        </div>

        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontSize: 22,
          fontWeight: 700,
          color: 'var(--text)',
          textAlign: 'center',
          margin: '0 0 8px',
        }}>
          Built for solo developers
        </h1>
        <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: 13,
          color: 'var(--text-secondary)',
          textAlign: 'center',
          margin: '0 0 24px',
        }}>
          AI-driven SDLC, sized for one. Free Individual plan.
        </p>

        <ul
          data-testid="individual-value-bullets"
          style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          {valueBullets.map(({ title, body }) => (
            <li
              key={title}
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                padding: '10px 12px',
                background: 'rgba(124,58,237,0.04)',
                border: '1px solid rgba(124,58,237,0.12)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              <span aria-hidden="true" style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '0 0 auto',
                width: 18,
                height: 18,
                marginTop: 1,
                borderRadius: 9,
                background: 'var(--primary)',
                color: 'white',
              }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </span>
              <div>
                <p style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)', margin: 0 }}>
                  {title}
                </p>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: '2px 0 0', lineHeight: 1.45 }}>
                  {body}
                </p>
              </div>
            </li>
          ))}
        </ul>

        {/* #3023 — same consent control as /register (label text, aria-label and
            the #2505 links to the published documents). */}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 16, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={e => setTermsAccepted(e.target.checked)}
            aria-label="I agree to the Terms of Service and Privacy Policy"
            style={{ marginTop: 3, flexShrink: 0, width: 16, height: 16 }}
          />
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
            I agree to the{' '}
            <a href="https://blueprintsdlc.com/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="https://blueprintsdlc.com/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>
              Privacy Policy
            </a>
          </span>
        </label>

        <OAuthButtons termsAccepted={termsAccepted} />

        <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center', margin: '14px 0 0', lineHeight: 1.5 }}>
          No password required. Sign in with the same account anytime.
        </p>

        <div style={{ height: 1, background: 'var(--border)', margin: '20px 0' }} />

        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', margin: 0 }}>
          Onboarding a team?{' '}
          <Link
            to="/register"
            data-testid="link-to-sme-register"
            style={{ color: 'var(--primary)', fontWeight: 500, textDecoration: 'none' }}
            onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
            onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
          >
            Sign up for Starter
          </Link>
        </p>

        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 8 }}>
          Already have an account?{' '}
          <Link
            to="/login"
            data-testid="link-to-login"
            style={{ color: 'var(--primary)', fontWeight: 500, textDecoration: 'none' }}
            onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
            onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
          >
            Sign in
          </Link>
        </p>

        <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 28, opacity: 0.6 }}>
          &copy; {new Date().getFullYear()} BluePrint &middot; Built with &#9829; by Ambai
        </p>
      </div>
    </div>
  );
}
